// ABOUTME: Provides display, selection, and eligibility helpers for the WhatsApp recruiter inbox.
// ABOUTME: Keeps filter behavior and chat send state decisions outside the page component.
export {
    maskWhatsappContactEpisodePhone,
    normalizeWhatsappContactEpisodeMode,
    shouldShowWhatsappContactEpisodeInbox
} from "functions/whatsappContactEpisode";
export type { WhatsappContactEpisodeMode } from "functions/whatsappContactEpisode";

export const normalizeWhatsappInboxLanguage = (language?: string) =>
    String(language || "")
        .toLowerCase()
        .startsWith("pt")
        ? "pt-BR"
        : "en-US";

export const shouldIgnoreWhatsappApplicantSelection = (
    currentApplicantId: any,
    nextApplicantId: any
) =>
    Boolean(nextApplicantId) &&
    String(currentApplicantId) === String(nextApplicantId);

export const shouldIgnoreWhatsappConversationSelection = (
    currentConversationKey: string,
    nextConversationKey: string,
    currentUsesInboxFilters: boolean,
    nextUsesInboxFilters: boolean
) =>
    Boolean(nextConversationKey) &&
    currentConversationKey === nextConversationKey &&
    currentUsesInboxFilters === nextUsesInboxFilters;

export interface WhatsappInboxFilterOption {
    value: string;
    label: string;
    email?: string;
    jobCount?: number;
    conversationCount?: number;
}

export interface WhatsappInboxFilterOptions {
    branches: WhatsappInboxFilterOption[];
    stores: WhatsappInboxFilterOption[];
    regions: WhatsappInboxFilterOption[];
    mainRecruiters: WhatsappInboxFilterOption[];
}

export const normalizeWhatsappInboxFilterOptions = (
    value: Compleo.IObject = {}
): WhatsappInboxFilterOptions => ({
    branches: Array.isArray(value.branches) ? value.branches : [],
    stores: Array.isArray(value.stores) ? value.stores : [],
    regions: Array.isArray(value.regions) ? value.regions : [],
    mainRecruiters: Array.isArray(value.mainRecruiters)
        ? value.mainRecruiters
        : []
});

export const shouldRefreshWhatsappInboxFilterOptions = (params: {
    companyId?: number;
    isLoading: boolean;
}) => Boolean(params.companyId) && !params.isLoading;

export const resolveWhatsappInboxErrorMessage = (
    error: any,
    translate: (key: string) => string,
    fallback: string
) => {
    const translationCode = String(
        error?.response?.data?.translationCode || ""
    ).trim();
    if (translationCode) {
        const translated = translate(translationCode);
        if (translated && translated !== translationCode) {
            return translated;
        }
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        fallback
    );
};

export interface WhatsappInboxAppliedFilters {
    search: string;
    jobIds: string[];
    jobCodes: string[];
    storeIds: string[];
    regionIds: string[];
    mainRecruiterEmails: string[];
    startedByCurrentUser: boolean;
    tags: string[];
}

export interface WhatsappInboxDraftFilters {
    jobs: Compleo.IObject[];
    stores: WhatsappInboxFilterOption[];
    regions: WhatsappInboxFilterOption[];
    recruiters: WhatsappInboxFilterOption[];
    startedByCurrentUser: boolean;
}

export const mergeWhatsappInboxStoreOptions = (
    branchOptions: WhatsappInboxFilterOption[] = [],
    storeOptions: WhatsappInboxFilterOption[] = []
) => {
    const options = new Map<string, WhatsappInboxFilterOption>();
    const merge = (
        option: WhatsappInboxFilterOption,
        source: "JOB" | "PRE_REGISTRATION"
    ) => {
        const value = String(option.value || "")
            .trim()
            .toLowerCase();
        if (!value) {
            return;
        }
        const current = options.get(value) || {
            value,
            label: String(option.label || value),
            jobCount: 0,
            conversationCount: 0
        };
        options.set(value, {
            ...current,
            label:
                source === "PRE_REGISTRATION"
                    ? String(option.label || current.label)
                    : current.label,
            jobCount:
                source === "JOB"
                    ? Number(option.jobCount || 0)
                    : current.jobCount || 0,
            conversationCount:
                source === "PRE_REGISTRATION"
                    ? Number(option.conversationCount || 0)
                    : current.conversationCount || 0
        });
    };

    branchOptions.forEach((option) => merge(option, "JOB"));
    storeOptions.forEach((option) => merge(option, "PRE_REGISTRATION"));

    return Array.from(options.values()).sort(
        (left, right) =>
            left.label.localeCompare(right.label) ||
            left.value.localeCompare(right.value)
    );
};

export const reconcileWhatsappInboxDraftFilters = (
    draft: WhatsappInboxDraftFilters,
    changedFilter: "JOB" | "STORE" | "REGION" | "RECRUITER" | "STARTED_BY"
): WhatsappInboxDraftFilters => {
    if (changedFilter === "REGION") {
        return {
            ...draft,
            jobs: [],
            recruiters: [],
            startedByCurrentUser: false
        };
    }
    if (
        changedFilter === "JOB" ||
        changedFilter === "RECRUITER" ||
        changedFilter === "STARTED_BY"
    ) {
        return { ...draft, regions: [] };
    }

    return draft;
};

export const buildWhatsappInboxFilterRequest = (
    filters: WhatsappInboxAppliedFilters
): WhatsappInboxAppliedFilters => ({
    search: filters.search,
    jobIds: filters.jobIds,
    jobCodes: filters.jobCodes,
    storeIds: filters.storeIds,
    regionIds: filters.regionIds,
    mainRecruiterEmails: filters.mainRecruiterEmails,
    startedByCurrentUser: filters.startedByCurrentUser,
    tags: filters.tags
});

export const getWhatsappInboxConversationFilters = (
    filters: WhatsappInboxAppliedFilters,
    useInboxFilters: boolean
) => (useInboxFilters ? buildWhatsappInboxFilterRequest(filters) : {});

export interface WhatsappInboxConversationIdentity {
    key: string;
    applicantId: string;
    segmentId: string;
    contactEpisodeId?: string;
    stateRevision?: number;
}

export interface WhatsappInboxRequestScope {
    companyId?: any;
    applicantId?: any;
    segmentId?: any;
    conversationKey?: any;
}

export const isWhatsappInboxRequestScopeCurrent = (
    request: WhatsappInboxRequestScope,
    current: WhatsappInboxRequestScope
) =>
    String(request.companyId || "").trim() ===
        String(current.companyId || "").trim() &&
    String(request.applicantId || "").trim() ===
        String(current.applicantId || "").trim() &&
    String(request.segmentId || "").trim() ===
        String(current.segmentId || "").trim() &&
    String(request.conversationKey || "").trim() ===
        String(current.conversationKey || "").trim();

export const applyWhatsappInboxResponseIfCurrent = (
    request: WhatsappInboxRequestScope,
    current: WhatsappInboxRequestScope,
    apply: () => void
) => {
    if (!isWhatsappInboxRequestScopeCurrent(request, current)) {
        return false;
    }

    apply();
    return true;
};

export const getWhatsappContactEpisodeConversationReference = (
    contactEpisodeId: any
) => ({
    scope: "CONTACT_EPISODE" as const,
    contactEpisodeId: String(contactEpisodeId || "").trim()
});

const getWhatsappContactEpisodeId = (conversation: Compleo.IObject = {}) => {
    const reference = conversation.conversationReference || {};
    const scope = String(conversation.scope || reference.scope || "").trim();
    if (scope !== "CONTACT_EPISODE") {
        return "";
    }

    return String(
        conversation.contactEpisodeId || reference.contactEpisodeId || ""
    ).trim();
};

export const getWhatsappInboxConversationKey = (
    conversation: Compleo.IObject = {}
) => {
    const contactEpisodeId = getWhatsappContactEpisodeId(conversation);
    if (contactEpisodeId) {
        return `CONTACT_EPISODE:${contactEpisodeId}`;
    }

    const segmentId = String(conversation.segmentId || "").trim();
    if (segmentId) {
        return `PRE:${segmentId}`;
    }

    const applicantId = String(conversation.applicantId || "").trim();
    return applicantId ? `JOB:${applicantId}` : "";
};

export const getWhatsappInboxConversationIdentity = (
    conversation: Compleo.IObject = {}
): WhatsappInboxConversationIdentity => {
    const contactEpisodeId = getWhatsappContactEpisodeId(conversation);
    const identity = {
        key: getWhatsappInboxConversationKey(conversation),
        applicantId: String(conversation.applicantId || "").trim(),
        segmentId: String(conversation.segmentId || "").trim()
    };

    if (!contactEpisodeId) {
        return identity;
    }

    return {
        ...identity,
        applicantId: "",
        segmentId: "",
        contactEpisodeId,
        stateRevision: Number(conversation.stateRevision || 0)
    };
};

export const shouldApplyWhatsappContactEpisodeResponse = (
    selection: { contactEpisodeId?: any; stateRevision?: any },
    response: { contactEpisodeId?: any; stateRevision?: any }
) =>
    String(selection.contactEpisodeId || "") ===
        String(response.contactEpisodeId || "") &&
    Number(response.stateRevision) >= Number(selection.stateRevision);

export const isSameWhatsappContactEpisode = (
    left: { contactEpisodeId?: any },
    right: { contactEpisodeId?: any }
) =>
    Boolean(left.contactEpisodeId) &&
    String(left.contactEpisodeId) === String(right.contactEpisodeId || "");

export const getWhatsappInboxTimelineKey = (
    conversation: { applicantId?: any; segmentId?: any } = {}
) => {
    const applicantId = String(conversation.applicantId || "").trim();
    return applicantId
        ? `APPLICANT:${applicantId}`
        : getWhatsappInboxConversationKey(conversation);
};

const getWhatsappInboxSnapshotTimestamp = (value: any) => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    const normalized = String(value || "").trim();
    if (!normalized) {
        return null;
    }

    const numericValue = Number(normalized);
    if (Number.isFinite(numericValue)) {
        return numericValue;
    }

    const parsedValue = Date.parse(normalized);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

interface WhatsappInboxReconciledSource {
    conversation: Compleo.IObject;
    identityKey: string;
    occurrence: number;
}

const isNewerWhatsappInboxSnapshot = (
    candidate: WhatsappInboxReconciledSource,
    current: WhatsappInboxReconciledSource
) => {
    const candidateUpdatedAt = getWhatsappInboxSnapshotTimestamp(
        candidate.conversation.updatedAt
    );
    const currentUpdatedAt = getWhatsappInboxSnapshotTimestamp(
        current.conversation.updatedAt
    );

    if (candidateUpdatedAt === null || currentUpdatedAt === null) {
        return candidate.occurrence > current.occurrence;
    }

    return (
        candidateUpdatedAt > currentUpdatedAt ||
        (candidateUpdatedAt === currentUpdatedAt &&
            candidate.occurrence > current.occurrence)
    );
};

const getLatestWhatsappInboxSource = (
    sources: WhatsappInboxReconciledSource[]
) =>
    sources.reduce((latest, source) => {
        const latestMessageAt = getWhatsappInboxSnapshotTimestamp(
            latest.conversation.lastMessageAt
        );
        const sourceMessageAt = getWhatsappInboxSnapshotTimestamp(
            source.conversation.lastMessageAt
        );
        if (sourceMessageAt === null && latestMessageAt !== null) {
            return latest;
        }
        if (sourceMessageAt !== null && latestMessageAt === null) {
            return source;
        }
        if (sourceMessageAt === null && latestMessageAt === null) {
            return source.occurrence > latest.occurrence ? source : latest;
        }
        return sourceMessageAt > latestMessageAt ||
            (sourceMessageAt === latestMessageAt &&
                source.occurrence > latest.occurrence)
            ? source
            : latest;
    }, sources[0]);

export const groupWhatsappInboxConversations = (
    conversations: Compleo.IObject[] = []
) => {
    const sourceConversations = conversations.flatMap((conversation) => {
        const sources = conversation.sourceConversations;
        return Array.isArray(sources) && sources.length > 0
            ? sources
            : [conversation];
    });
    const reconciledSources = new Map<string, WhatsappInboxReconciledSource>();

    sourceConversations.forEach((conversation, occurrence) => {
        const identityKey = getWhatsappInboxConversationKey(conversation);
        if (!identityKey) {
            return;
        }

        const candidate = { conversation, identityKey, occurrence };
        const current = reconciledSources.get(identityKey);
        if (!current || isNewerWhatsappInboxSnapshot(candidate, current)) {
            reconciledSources.set(identityKey, candidate);
        }
    });

    const grouped = new Map<string, Compleo.IObject>();

    const sourcesByGroup = new Map<string, WhatsappInboxReconciledSource[]>();
    Array.from(reconciledSources.values()).forEach((source) => {
        const conversation = source.conversation;
        const applicantId = String(conversation.applicantId || "").trim();
        const groupKey = applicantId
            ? `APPLICANT:${applicantId}`
            : source.identityKey;
        if (!groupKey) {
            return;
        }

        const nextSources = [...(sourcesByGroup.get(groupKey) || []), source];
        sourcesByGroup.set(groupKey, nextSources);
        const latest = getLatestWhatsappInboxSource(nextSources).conversation;
        grouped.set(groupKey, {
            ...latest,
            applicantId,
            ...(applicantId ? { segmentId: "" } : {}),
            scope: applicantId ? "APPLICANT_TIMELINE" : latest.scope,
            inboxKey: groupKey,
            unreadCount: nextSources.reduce(
                (total: number, source: WhatsappInboxReconciledSource) =>
                    total + Number(source.conversation.unreadCount || 0),
                0
            ),
            sourceConversations: nextSources.map(
                (source) => source.conversation
            )
        });
    });

    return Array.from(grouped.values()).sort((left, right) =>
        String(right.lastMessageAt || "").localeCompare(
            String(left.lastMessageAt || "")
        )
    );
};

export const markWhatsappInboxTimelineAsRead = (
    conversations: Compleo.IObject[] = [],
    timelineKey = ""
) =>
    conversations.map((conversation) => {
        if (
            !timelineKey ||
            getWhatsappInboxTimelineKey(conversation) !== timelineKey
        ) {
            return conversation;
        }

        return {
            ...conversation,
            unreadCount: 0,
            ...(Array.isArray(conversation.sourceConversations)
                ? {
                      sourceConversations:
                          conversation.sourceConversations.map(
                              (source: Compleo.IObject) => ({
                                  ...source,
                                  unreadCount: 0
                              })
                          )
                  }
                : {})
        };
    });

export interface WhatsappInboxActionContext {
    scope: "JOB" | "PRE_REGISTRATION";
    segmentId?: string;
    storeId?: string;
    storeName?: string;
}

export interface WhatsappInboxActionTarget {
    applicantId: string;
    segmentId?: string;
    conversationScope?: "APPLICANT_TIMELINE";
}

export const getWhatsappInboxActionContextKey = (
    context: WhatsappInboxActionContext
) =>
    context.scope === "PRE_REGISTRATION"
        ? `PRE:${String(context.segmentId || "")}`
        : "JOB";

const getWhatsappInboxTimelineItemKey = (item: Compleo.IObject) => {
    const identity = [
        ["MESSAGE", item.messageId],
        ["NOTE", item.noteId],
        ["ID", item._id],
        ["SORT", item.skGS1pk]
    ].find(([, value]) => String(value || "").trim());

    return identity
        ? `${identity[0]}:${String(identity[1]).trim()}`
        : "";
};

const getWhatsappInboxTimelineItemDate = (item: Compleo.IObject) => {
    const parsed = new Date(item.createdAt || item.updatedAt || "").getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

const sortWhatsappInboxTimelineItems = (items: Compleo.IObject[]) =>
    [...items].sort((left, right) => {
        const byTimestamp =
            Number(left.metaTimestamp || 0) -
            Number(right.metaTimestamp || 0);
        if (byTimestamp !== 0) {
            return byTimestamp;
        }

        const byDate =
            getWhatsappInboxTimelineItemDate(left) -
            getWhatsappInboxTimelineItemDate(right);
        if (byDate !== 0) {
            return byDate;
        }

        return getWhatsappInboxTimelineItemKey(left).localeCompare(
            getWhatsappInboxTimelineItemKey(right)
        );
    });

export const mergeWhatsappInboxTimelinePages = (
    current: Compleo.IObject = {},
    next: Compleo.IObject = {}
) => {
    const contexts = new Map<string, WhatsappInboxActionContext>();
    [
        ...(Array.isArray(current.contexts) ? current.contexts : []),
        ...(Array.isArray(next.contexts) ? next.contexts : [])
    ].forEach((context: WhatsappInboxActionContext) => {
        contexts.set(getWhatsappInboxActionContextKey(context), context);
    });

    const keyedItems = new Map<string, Compleo.IObject>();
    const unkeyedItems: Compleo.IObject[] = [];
    [
        ...(Array.isArray(current.items) ? current.items : []),
        ...(Array.isArray(next.items) ? next.items : [])
    ].forEach((item: Compleo.IObject) => {
        const key = getWhatsappInboxTimelineItemKey(item);
        if (key) {
            keyedItems.set(key, item);
        } else {
            unkeyedItems.push(item);
        }
    });

    const mergedContexts = Array.from(contexts.values());
    return {
        ...current,
        ...next,
        channel: current.channel || next.channel,
        contexts: mergedContexts,
        items: sortWhatsappInboxTimelineItems([
            ...keyedItems.values(),
            ...unkeyedItems
        ]),
        unreadCount:
            Number(current.unreadCount || 0) +
            Number(next.unreadCount || 0),
        contextsTruncated: next.contextsTruncated === true,
        nextContextCursor: String(next.nextContextCursor || "")
    };
};

export const shouldScheduleWhatsappInboxRefresh = (
    refreshTimer: number | null
) => refreshTimer === null;

export const getWhatsappInboxActionTarget = (
    applicantId: any,
    actionContext: WhatsappInboxActionContext | null
): WhatsappInboxActionTarget => {
    const normalizedApplicantId = String(applicantId || "").trim();
    if (normalizedApplicantId) {
        return {
            applicantId: normalizedApplicantId,
            conversationScope: "APPLICANT_TIMELINE" as const
        };
    }

    return {
        applicantId: "",
        segmentId: actionContext?.segmentId || undefined
    };
};

export const getWhatsappInboxActionConversationData = (
    conversationData: Compleo.IObject = {},
    applicantId: any = ""
) =>
    String(applicantId || "").trim()
        ? conversationData.channel || {}
        : conversationData;

export const buildWhatsappInboxTimelineRequest = (
    applicantId: any,
    segmentId: any
) => {
    const normalizedApplicantId = String(applicantId || "").trim();
    if (normalizedApplicantId) {
        return {
            applicantId: normalizedApplicantId,
            view: "APPLICANT_TIMELINE" as const
        };
    }

    return {
        applicantId: "",
        segmentId: String(segmentId || "").trim() || undefined
    };
};

export const getWhatsappMessageActionContext = (
    item: { conversationSegmentId?: any } = {}
): WhatsappInboxActionContext => {
    const segmentId = String(item.conversationSegmentId || "").trim();
    return segmentId
        ? { scope: "PRE_REGISTRATION", segmentId }
        : { scope: "JOB" };
};

export const getCurrentWhatsappConversationData = (
    conversationData: Compleo.IObject = {},
    selectedApplicantId: any = "",
    selectedSegmentId: any = ""
) => {
    const expectedKey = getWhatsappInboxConversationKey({
        applicantId: selectedApplicantId,
        segmentId: selectedSegmentId
    });
    if (!expectedKey) {
        return {};
    }

    const detailKey = getWhatsappInboxConversationKey({
        applicantId:
            conversationData.applicantId ||
            conversationData.candidate?.applicantId,
        segmentId: conversationData.segmentId
    });

    return detailKey === expectedKey ? conversationData : {};
};

export const canSendWhatsappSessionMessage = (
    conversationData: Compleo.IObject = {},
    fallbackConversation: Compleo.IObject = {},
    selectedApplicantId: any = "",
    selectedSegmentId: any = ""
) => {
    const currentConversationData =
        selectedApplicantId || selectedSegmentId
            ? getCurrentWhatsappConversationData(
                  conversationData,
                  selectedApplicantId,
                  selectedSegmentId
              )
            : conversationData;

    if (
        Object.prototype.hasOwnProperty.call(
            currentConversationData,
            "canSendSessionMessage"
        )
    ) {
        return currentConversationData.canSendSessionMessage === true;
    }

    return fallbackConversation.canSendSessionMessage === true;
};

export const getWhatsappConversationBooleanState = (
    conversationData: Compleo.IObject = {},
    fallbackConversation: Compleo.IObject = {},
    key: string
) => {
    if (Object.prototype.hasOwnProperty.call(conversationData, key)) {
        return conversationData[key] === true;
    }

    return fallbackConversation[key] === true;
};

export const shouldClearWhatsappApplicantSelection = (
    selectedApplicantId: any,
    conversations: Array<{ applicantId?: any }>,
    filters: Compleo.IObject
) => {
    if (!selectedApplicantId) {
        return false;
    }

    const hasActiveFilters = Object.values(filters || {}).some((value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (typeof value === "boolean") {
            return value === true;
        }

        return String(value || "").trim().length > 0;
    });
    if (!hasActiveFilters) {
        return false;
    }

    return !conversations.some(
        (conversation) =>
            String(conversation.applicantId) === String(selectedApplicantId)
    );
};

export const shouldClearWhatsappConversationSelection = (
    selectedConversationKey: string,
    conversations: Array<{ applicantId?: any; segmentId?: any }>,
    filters: Compleo.IObject
) => {
    if (!selectedConversationKey) {
        return false;
    }

    const hasActiveFilters = Object.values(filters || {}).some((value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (typeof value === "boolean") {
            return value === true;
        }
        return String(value || "").trim().length > 0;
    });
    if (!hasActiveFilters) {
        return false;
    }

    return !conversations.some(
        (conversation) =>
            String(conversation.inboxKey || "") === selectedConversationKey ||
            getWhatsappInboxTimelineKey(conversation) ===
            selectedConversationKey
    );
};

export const getDisplayText = (value: any, language = "pt-BR"): string => {
    if (value === null || value === undefined) {
        return "";
    }

    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return String(value).trim();
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => getDisplayText(item, language))
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") {
        const normalizedLanguage = normalizeWhatsappInboxLanguage(language);
        const alternateLanguage =
            normalizedLanguage === "pt-BR" ? "en-US" : "pt-BR";
        const candidateKeys = [
            `label-${normalizedLanguage}`,
            "label",
            `label-${alternateLanguage}`,
            "value",
            "name",
            "title",
            "jobTitle",
            "JobTitle"
        ];

        for (const key of candidateKeys) {
            const text = getDisplayText(value[key], language);
            if (text) {
                return text;
            }
        }
    }

    return "";
};

export const getWhatsappInboxPhoneDisplay = (
    value: any,
    language = "pt-BR"
) => {
    const phone = getDisplayText(value, language);
    const digits = phone.replace(/\D/g, "");

    return /^[+]?[-().\s\d]+$/.test(phone) &&
        digits.length >= 8 &&
        digits.length <= 15
        ? phone
        : "";
};

export const getWhatsappInboxProfileContactDisplay = (
    email: any,
    mobileNumber: any,
    language = "pt-BR"
) => {
    const contact = [
        getDisplayText(email, language),
        getWhatsappInboxPhoneDisplay(mobileNumber, language)
    ].filter(Boolean);

    return contact.length ? contact.join(" · ") : "-";
};

export const getFirstDisplayText = (language: string, ...values: any[]) => {
    for (const value of values) {
        const text = getDisplayText(value, language);
        if (text) {
            return text;
        }
    }

    return "";
};

export const getWhatsappInboxConversationName = (
    applicantName: any,
    applicantEmail: any,
    language = "pt-BR"
) =>
    getFirstDisplayText(language, applicantName, applicantEmail);

export const getConversationTags = (
    conversation: Compleo.IObject,
    language: string
) =>
    Array.isArray(conversation.tags)
        ? (conversation.tags as any[])
              .map((tag) => getDisplayText(tag, language))
              .filter(Boolean)
        : [];

export const getJobTitle = (job: Compleo.IObject, language: string) =>
    getFirstDisplayText(
        language,
        job.title,
        job.jobTitle,
        job.JobTitle,
        job.jobId
    );

export const getJobLabel = (job: Compleo.IObject, language: string) => {
    const title = getJobTitle(job, language);
    const jobId = getDisplayText(job.jobId, language);

    if (!title) {
        return jobId;
    }

    return jobId && jobId !== title ? `${title} (${jobId})` : title;
};

export const moveTagsToFront = (
    current: string[],
    tags: any[],
    language: string
) => {
    const normalizedTags = tags
        .map((tag) => getDisplayText(tag, language))
        .filter(Boolean);
    const front = Array.from(new Set(normalizedTags));
    const rest = current.filter((tag) => !front.includes(tag));
    return [...front, ...rest];
};

export const normalizeTagList = (tags: any[] = [], language = "pt-BR") =>
    Array.from(
        new Set(
            tags
                .map((tag) => getDisplayText(tag, language))
                .map((tag) => tag.trim())
                .filter(Boolean)
        )
    );

export const formatWhatsappInboxDate = (value?: string, language = "pt-BR") => {
    if (!value || value === "0") {
        return "-";
    }

    const numeric = Number(value);
    const date = Number.isFinite(numeric)
        ? new Date(numeric * 1000)
        : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString(normalizeWhatsappInboxLanguage(language));
};

export const canOpenWhatsappMedia = (message: Compleo.IObject = {}) =>
    ["document", "image"].includes(String(message.type || "")) &&
    Boolean(message.mediaS3Key || message.hasMedia);

export const formatWhatsappReactionText = (
    message: Compleo.IObject = {},
    labels: { reactedWith?: string } = {}
) => {
    if (String(message.type || "") !== "reaction") {
        return "";
    }

    const emoji = String(message.reactionEmoji || "").trim();
    if (!emoji) {
        return String(message.textContent || "").trim();
    }

    return String(labels.reactedWith || "Reagiu com {{emoji}}").replace(
        "{{emoji}}",
        emoji
    );
};
